
import React from 'react';
import ReactDOM from 'react-dom/client';
import { PostHogProvider } from 'posthog-js/react';
import App from './App.tsx';

/**
 * Robust environment variable resolver.
 * Handles different platform conventions and avoids bundler static-replacement issues.
 */
const getEnvironmentVariable = (key: string): string => {
  const win = window as any;
  
  // 1. Literal checks for the most common Vite/Vercel public keys
  // This helps when bundlers statically replace these strings
  if (key === 'VITE_PUBLIC_POSTHOG_KEY') {
    // @ts-ignore
    const val = typeof import.meta !== 'undefined' && import.meta.env?.VITE_PUBLIC_POSTHOG_KEY;
    if (val) return val;
  }
  if (key === 'VITE_POSTHOG_KEY') {
    // @ts-ignore
    const val = typeof import.meta !== 'undefined' && import.meta.env?.VITE_POSTHOG_KEY;
    if (val) return val;
  }

  // 2. Check import.meta.env (Vite/Modern ESM)
  try {
    // @ts-ignore
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
      // @ts-ignore
      return import.meta.env[key] as string;
    }
  } catch (e) {}

  // 3. Check process.env (Standard Node/Webpack)
  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key] as string;
    }
  } catch (e) {}

  // 4. Check window.process.env (Platform Shim)
  try {
    if (win.process?.env?.[key]) {
      return win.process.env[key];
    }
  } catch (e) {}

  return '';
};

// Search for the key using all common conventions
const posthogKey = 
  getEnvironmentVariable('VITE_PUBLIC_POSTHOG_KEY') || 
  getEnvironmentVariable('VITE_POSTHOG_KEY') || 
  getEnvironmentVariable('POSTHOG_KEY') || 
  getEnvironmentVariable('NEXT_PUBLIC_POSTHOG_KEY');

const posthogHost = 
  getEnvironmentVariable('VITE_PUBLIC_POSTHOG_HOST') || 
  getEnvironmentVariable('VITE_POSTHOG_HOST') || 
  getEnvironmentVariable('POSTHOG_HOST') || 
  'https://eu.i.posthog.com';

const posthogOptions = {
  api_host: posthogHost,
  person_profiles: 'identified_only' as const,
  capture_pageview: true,
};

console.log("🚀 Starting Homework Bridge App...");

const rootElement = document.getElementById('root');
if (!rootElement) {
  console.error("❌ Root element not found!");
  throw new Error("Root element not found");
}

console.log("✅ Root element found");

const root = ReactDOM.createRoot(rootElement);
console.log("✅ React root created");

// Validation logic
const isValidKey = posthogKey && posthogKey !== 'phc_placeholder' && posthogKey.length > 10;

try {
  if (isValidKey) {
    console.log("✅ PostHog connected. Host:", posthogHost);
    root.render(
      <React.StrictMode>
        <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
          <App />
        </PostHogProvider>
      </React.StrictMode>
    );
  } else {
    console.warn("⚠️ PostHog: No valid API Key found. App will run without analytics.");
    console.warn("💡 ACTION REQUIRED: In your Vercel Project Settings, rename 'POSTHOG_KEY' to 'VITE_PUBLIC_POSTHOG_KEY'. Then trigger a new deployment.");
    
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }
  console.log("✅ React app rendered successfully");
} catch (error) {
  console.error("❌ Error rendering app:", error);
  rootElement.innerHTML = `
    <div style="padding: 20px; font-family: sans-serif;">
      <h1>Error Loading App</h1>
      <p>There was an error loading the application. Please check the console for details.</p>
      <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow: auto;">${error}</pre>
    </div>
  `;
  throw error;
}
